import { Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { KC_NAME, CHART_TITLE_KC } from '../../../constants/graphs';
import apiClient from '../../../requests/api/apiClient';
import {
  ConversationSchema,
  TimePointSchema,
  CultivationCyclePopulatedSchema,
  PhenologicalStateWithCycle,
  GetCultivationCyclesBySectorResponse,
  PhenologicalStateSectorResponse,
} from '../../../requests/api/apiTypes';
import { ReduxState } from '../../../types';
import { navigateToChart } from '../../../utils/helpers';
import { FetchError } from '../../../views/types';
import BaseHighchart from '../BaseHighchart';
import ChartFilter from '../ChartFilter';
import ConversationModal from '../ConversationTab/conversationModal';
import ChartConfigDialog from '../ChartConfigDialog/ChartConfigDialog';
import KcConfigContent from './KcConfigContent';
import { ChartDataType, ChartFiltersDouble } from '../types';
import prepareData from './builder';
import useFetchApi from '../../../hooks/useFetchApi';
import { getKcColor } from '../utils/colorUtils';
import { getConversationTooltip } from '../LineCharts/utils';
import useChartConfigClick from '../../../hooks/useChartConfigClick';

function KcChart({
  chartView,
  sectorId,
  farmId,
  hideTitle = false,
  hideLegend = false,
  hideYValues = false,
  height = '300',
  title = CHART_TITLE_KC,
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
  hideXValues = false,
  yAxisTitle = 'Kc',
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideTitle?: boolean
  hideLegend?: boolean
  hideYValues?: boolean
  height?: string
  title?: string
  yAxisTitle?: string
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
  hideXValues?: boolean;
}) {
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  const chartName = 'kc';
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);

  // State for cultivation cycle and phenological states
  const [selectedCycle, setSelectedCycle] = useState<CultivationCyclePopulatedSchema | null>(
    null,
  );
  const [
    phenologicalStatesByCycle,
    setPhenologicalStatesByCycle] = useState<PhenologicalStateWithCycle>({});
  const [updateCounter, setUpdateCounter] = useState(0);

  const { data: cultivationCyclesData } = useFetchApi<GetCultivationCyclesBySectorResponse>({
    modelEndpoint: 'cultivationCycleSectorDetail',
    modelName: 'cultivationCycleSector',
    id: sectorId,
    dependencies: [sectorId],
  });

  const { data: phenologicalStatesData } = useFetchApi<PhenologicalStateSectorResponse>({
    modelEndpoint: 'phenologicalStateSectorList',
    modelName: 'phenologicalState',
    id: sectorId,
    dependencies: [sectorId],
  });

  // Force update when sector configuration changes
  useEffect(() => {
    if (selectedSector?.irrigationConfig?.selectedCultivationCycleId) {
      setUpdateCounter((prev) => prev + 1);
    }
  }, [selectedSector?.irrigationConfig?.selectedCultivationCycleId]);

  useEffect(() => {
    if (cultivationCyclesData && selectedSector) {
      const savedCycleId = selectedSector.irrigationConfig?.selectedCultivationCycleId;
      const savedCycle = savedCycleId
        ? cultivationCyclesData.cultivationCycles.find(
          (c: CultivationCyclePopulatedSchema) => c.id === savedCycleId,
        )
        : null;
      setSelectedCycle(savedCycle || cultivationCyclesData.cultivationCycles[0] || null);
    }
  }, [cultivationCyclesData, selectedSector, updateCounter]);

  useEffect(() => {
    if (phenologicalStatesData) {
      setPhenologicalStatesByCycle(phenologicalStatesData.phenologicalStates);
    }
  }, [phenologicalStatesData]);

  const getKcColorWithState = useCallback(
    (kcValue: number, date?: string) => getKcColor(
      kcValue,
      date,
      selectedCycle,
      phenologicalStatesByCycle,
    ),
    [selectedCycle, phenologicalStatesByCycle],
  );

  const preparedFilters: ChartFiltersDouble = useMemo(() => {
    if (!interval) {
      return {
        farmId,
        startDate: '',
        endDate: '',
        weatherZoneId: '',
      };
    }
    return {
      farmId,
      startDate: interval.startDate,
      endDate: interval.endDate,
      sectorId,
      weatherZoneId: selectedSector?.weatherZoneId,
      chart: KC_NAME,
    };
  }, [farmId, interval, selectedSector?.weatherZoneId]);

  const createSeriesData = useCallback((preparedData: any) => [
    {
      type: 'column',
      name: 'Kc',
      data: preparedData.kc.map((point: any) => ({
        ...point,
        color: getKcColorWithState(point.y, point.custom?.date),
      })),
      yAxis: 0,
      stack: 'kc',
    },
  ], [getKcColorWithState]);

  // To Do: Actualizar types
  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      if (interval) {
        // Validar que el sector tenga telemetría asignada
        if (!selectedSector?.telemetryType) {
          setIsLoading(false);
          return;
        }

        const preparedData = await prepareData(preparedFilters, selectedSector?.telemetryType);
        const seriesData = createSeriesData(preparedData);

        setChartData({
          series: seriesData,
          categories: preparedData.categories as string[],
          absoluteCategories: preparedData.absoluteCategories as string[],
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [preparedFilters, sectorId, interval, createSeriesData, updateCounter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customTooltip = useMemo(() => ({
    shared: true,
    useHTML: true,
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      if (this.series.name === 'Comentarios') {
        return getConversationTooltip(this, conversations);
      }
      const pointDate = this.points?.[0]?.point.custom?.date || '';
      const header = '<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">Resumen</div>';
      const subHeader = `<div style="font-size: 12px; margin-bottom: 8px;">${dayjs(this.x).format('MMM DD, YYYY')}</br></div>`;
      const points = this.points?.map((point) => {
        const kcValue = Number(point.y);
        const kcColor = getKcColorWithState(kcValue, pointDate);
        return `<div style="margin: 4px 0"><span style="color:${kcColor}">\u25CF</span> ${point.series.name}: <b>${kcValue.toFixed(2)}</b></div>`;
      }).join('');
      return `<div style="padding: 8px">${header}${subHeader}${points}</div>`;
    },
  }), [conversations, getKcColorWithState]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            KC_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  const xMin = useMemo(() => (
    interval?.startDate ? dayjs(interval.startDate).valueOf() : undefined), [interval]);
  const xMax = useMemo(() => (
    interval?.endDate ? dayjs(interval.endDate).valueOf() : undefined), [interval]);

  const chartConfig = useMemo(() => ({
    chart: {
      height,
    },
    xAxis: {
      type: 'datetime',
      min: xMin,
      max: xMax,
      labels: {
        format: '{value:%b %d}',
        enabled: !hideXValues,
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: yAxisTitle || null,
      },
    },
    plotOptions: {
      column: {
        pointPadding: 0,
        borderWidth: 0,
      },
    },
  }), [height, xMin, xMax, hideXValues]);

  return (
    <>
      <Box style={{ width: '100%' }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}
        <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          <BaseHighchart
            chartView={chartView}
            sectorId={sectorId}
            filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
            title={hideTitle ? '' : title}
            hideYValues={hideYValues}
            categories={chartData.categories}
            series={chartData.series as Highcharts.SeriesOptionsType[]}
            tooltip={customTooltip}
            chartConfig={chartConfig}
            hideXValues={hideXValues}
            setModalState={setModalState}
            conversations={conversations}
            absoluteCategories={chartData.absoluteCategories}
            legend={{ enabled: !hideLegend }}
            onConfigClick={handleConfigClick}
            onFullScreenClick={handleFullScreenClick}
            interval={interval}
            zoomSync={true}
            autoAdjustYAxis={true}
            yAxisTitle={yAxisTitle}
            graphName={KC_NAME}
          />
        </div>
      </Box>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={KC_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Coeficiente de cultivo"
      >
        <KcConfigContent sectorId={sectorId} />
      </ChartConfigDialog>
    </>
  );
}

export default KcChart;
